import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../services/api.js';

const ACTIVE_STATUSES = ['Submitted', 'Confirmed', 'Planned', 'Dispatched', 'In Progress'];

function fmt(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/**
 * Vessel Master — Arrival (mobile-first). The ship captain / agent's focused
 * screen: their current movement + ETA, a one-tap "Confirm Arrival" when the
 * vessel is near port (FR-VES-4 / A-8), and the tugs + pilot assigned to them.
 * Renders in the responsive shell; laid out single-column for a handheld.
 */
export default function VesselMasterArrivalPage() {
  const [bookings, setBookings] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  // 8881: read the current selection from a ref — a state updater queued behind
  // setBookings runs on the next render, too late for loadExecution.
  const selectedRef = useRef('');
  const [exec, setExec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [msg, setMsg] = useState(null);
  const [masterName, setMasterName] = useState('');
  const [confirmingAssist, setConfirmingAssist] = useState(false);

  const loadExecution = useCallback(async (id) => {
    if (!id) return setExec(null);
    try {
      setExec(await api.getBookingExecution(id));
    } catch {
      setExec(null);
    }
  }, []);

  const load = useCallback(async () => {
    const all = (await api.listBookings()) || [];
    // Current movements first, then finished assists still waiting for the
    // master's confirmation (whatever their status has since become).
    const byTime = (a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at);
    const current = all.filter((b) => ACTIVE_STATUSES.includes(b.status)).sort(byTime);
    const awaiting = all.filter((b) => b.assist_awaiting && !ACTIVE_STATUSES.includes(b.status)).sort(byTime);
    const active = [...current, ...awaiting];
    setBookings(active);
    // 8881: open on the movement dispatch has put tugs on, not an older one still being planned.
    const withTugs = current.find((b) => b.assigned_tugs);
    const prev = selectedRef.current;
    const chosen = prev && active.some((b) => b.id === prev) ? prev : (withTugs || active[0])?.id || '';
    selectedRef.current = chosen;
    setSelectedId(chosen);
    await loadExecution(chosen);
  }, [loadExecution]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        await load();
      } catch (err) {
        setLoadError(err.message || 'Failed to load your movements.');
      } finally {
        setLoading(false);
      }
    })();
  }, [load]);

  const booking = bookings.find((b) => b.id === selectedId) || null;
  const nearPort = booking?.ais_near_port; // Submitted + AIS approach (confirm-ready)

  const onSelect = async (id) => {
    selectedRef.current = id;
    setSelectedId(id);
    setMsg(null);
    await loadExecution(id);
  };

  const confirmArrival = async () => {
    if (!booking) return;
    setConfirming(true);
    setMsg(null);
    try {
      await api.confirmBooking(booking.id, {
        aisArrivalWindow: new Date().toISOString(),
        captainReadiness: true,
        pilotageRequired: booking.pilotage_required,
      });
      setMsg({ type: 'success', text: 'Arrival confirmed — port clearance provided. Dispatch is notified.' });
      await load();
    } catch (err) {
      setMsg({ type: 'danger', text: err.message || 'Failed to confirm arrival.' });
    } finally {
      setConfirming(false);
    }
  };

  // The digital stand-in for signing the tug's paper dispatch ticket.
  const confirmAssist = async () => {
    if (!booking) return;
    if (masterName.trim().length < 2) {
      setMsg({ type: 'danger', text: 'Enter your name to confirm the assist.' });
      return;
    }
    setConfirmingAssist(true);
    setMsg(null);
    try {
      await api.confirmAssist(booking.id, masterName.trim());
      setMsg({ type: 'success', text: 'Thank you — the assist is confirmed as completed.' });
      setMasterName('');
      await loadExecution(booking.id);
    } catch (err) {
      setMsg({ type: 'danger', text: err.details?.[0]?.message || err.message || 'Failed to confirm the assist.' });
    } finally {
      setConfirmingAssist(false);
    }
  };

  return (
    <div className="container-fluid py-3" data-testid="vessel-master-page" style={{ maxWidth: 560 }}>
      <div className="bg-primary text-white p-3 rounded shadow d-flex align-items-center mb-3">
        <i className="fas fa-anchor me-2 fa-lg" aria-hidden="true" />
        <div>
          <h6 className="mb-0 fw-bold">Vessel Master — Arrival</h6>
          <small className="opacity-75">Confirm arrival &amp; view your assigned tugs</small>
        </div>
      </div>

      {loadError && <div className="alert alert-danger" data-testid="vm-load-error">{loadError}</div>}
      {msg && <div className={`alert alert-${msg.type}`} role="alert" data-testid="vm-message">{msg.text}</div>}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading…</span></div>
        </div>
      ) : !booking ? (
        <div className="alert alert-secondary text-center" data-testid="vm-no-movement">
          <i className="fas fa-ship fa-2x d-block mb-2 text-secondary" aria-hidden="true" />
          No active movement. New bookings appear here as they approach port.
        </div>
      ) : (
        <>
          {bookings.length > 1 && (
            <div className="mb-3">
              <label htmlFor="vm-select" className="form-label small fw-bold text-muted">Your movements</label>
              <select
                id="vm-select"
                className="form-select"
                value={selectedId}
                onChange={(e) => onSelect(e.target.value)}
                data-testid="vm-select"
              >
                {bookings.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.booking_ref} — {b.vessel_name || 'Vessel'} ({b.movement_type})
                    {b.assist_awaiting ? ' — confirm assist' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Movement card */}
          <div className="card border-0 shadow-sm mb-3" data-testid="vm-movement">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <h5 className="mb-0 fw-bold">{booking.vessel_name || 'Vessel'}</h5>
                  <span className="text-muted small">{booking.booking_ref}</span>
                </div>
                <span className="badge bg-info text-dark" data-testid="vm-status">{booking.status}</span>
              </div>
              <ul className="list-group list-group-flush small">
                <li className="list-group-item d-flex justify-content-between px-0">
                  <span className="text-muted">Movement</span><span className="fw-bold">{booking.movement_type}</span>
                </li>
                <li className="list-group-item d-flex justify-content-between px-0">
                  <span className="text-muted">Port / Berth</span>
                  <span className="fw-bold">{exec?.berthName || exec?.portName || booking.port_name || '—'}</span>
                </li>
                <li className="list-group-item d-flex justify-content-between px-0">
                  <span className="text-muted">Scheduled / ETA</span><span className="fw-bold">{fmt(booking.scheduled_at)}</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Confirm arrival — big, near-port only */}
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body text-center">
              {booking.status === 'Submitted' ? (
                <>
                  <p className="small text-muted mb-2">
                    {nearPort
                      ? 'AIS shows your vessel approaching. Provide final clearance to confirm.'
                      : 'You can provide final arrival clearance for this movement.'}
                  </p>
                  <button
                    className="btn btn-success btn-lg w-100 fw-bold py-3"
                    onClick={confirmArrival}
                    disabled={confirming}
                    data-testid="vm-confirm-arrival"
                  >
                    {confirming && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />}
                    <i className="fas fa-check-circle me-2" aria-hidden="true" />
                    Confirm Arrival
                  </button>
                </>
              ) : (
                <div className="text-success fw-semibold" data-testid="vm-confirmed">
                  <i className="fas fa-check-circle me-2" aria-hidden="true" />
                  Arrival confirmed — movement is {booking.status}.
                </div>
              )}
            </div>
          </div>

          {/* Assist completed — once every tug has finished */}
          {exec?.assist?.confirmedAt ? (
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-body text-success fw-semibold" data-testid="vm-assist-confirmed">
                <i className="fas fa-check-double me-2" aria-hidden="true" />
                Assist confirmed as completed by {exec.assist.confirmedBy} on {fmt(exec.assist.confirmedAt)}.
              </div>
            </div>
          ) : exec?.assist?.done ? (
            <div className="card border-0 shadow-sm mb-3" data-testid="vm-assist-confirm">
              <div className="card-body">
                <h6 className="fw-bold mb-1">
                  <i className="fas fa-file-signature me-2 text-primary" aria-hidden="true" />
                  Confirm the assist was completed
                </h6>
                <p className="small text-muted mb-2">
                  The tug{exec.assignedTugs?.length > 1 ? 's have' : ' has'} finished
                  {exec.clock?.start ? ` (${fmt(exec.clock.start)} → ${fmt(exec.clock.stop)})` : ''}. Your
                  confirmation replaces signing the tug&apos;s dispatch ticket.
                </p>
                <label htmlFor="vm-master-name" className="form-label small fw-bold text-muted">
                  Your name (master or agent)
                </label>
                <input
                  id="vm-master-name"
                  className="form-control mb-2"
                  value={masterName}
                  maxLength={160}
                  onChange={(e) => setMasterName(e.target.value)}
                  data-testid="vm-master-name"
                />
                <button
                  className="btn btn-primary btn-lg w-100 fw-bold"
                  onClick={confirmAssist}
                  disabled={confirmingAssist}
                  data-testid="vm-confirm-assist"
                >
                  {confirmingAssist && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />}
                  Confirm Assist Completed
                </button>
              </div>
            </div>
          ) : null}

          {/* Assigned tugs + pilot */}
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-header bg-white pt-3 border-bottom-0 pb-1">
              <h6 className="mb-0 text-muted fw-bold text-uppercase small">
                <i className="fas fa-ship me-1 text-primary" aria-hidden="true" />
                Assigned Tugs &amp; Pilot
              </h6>
            </div>
            <div className="card-body pt-2">
              <div className="mb-2" data-testid="vm-tugs">
                {exec?.assignedTugs?.length ? (
                  exec.assignedTugs.map((t) => (
                    <span key={t.id} className="badge bg-primary me-1 mb-1 p-2">
                      <i className="fas fa-ship me-1" aria-hidden="true" />{t.name}
                    </span>
                  ))
                ) : (
                  <span className="text-muted small">Tugs not yet assigned — dispatch is planning your job.</span>
                )}
              </div>
              <div className="small" data-testid="vm-pilot">
                <span className="text-muted">Pilot: </span>
                <span className="fw-bold">
                  {exec?.pilot
                    ? `${exec.pilot.name || exec.pilot.status}${exec.pilot.status ? ` · ${exec.pilot.status}` : ''}`
                    : booking.pilotage_required ? 'Pending assignment' : 'Not required'}
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
